import type { ReactNode } from "react";
import { Button } from "./Button";
import { cx } from "./cx";
import s from "./table.module.css";

export interface Column<Row> {
  key: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  align?: "start" | "end";
  /** Visually hidden header text for icon-only columns. */
  srOnly?: boolean;
}

export interface TableProps<Row> {
  columns: ReadonlyArray<Column<Row>>;
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  label: string;
  /** Shows "Load more" when true. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  /** Left side of the footer, e.g. "6 of 40". */
  summary?: ReactNode;
  /** Cap the body height so the sticky header has something to stick to. */
  maxHeight?: string | number;
  minWidth?: number;
}

/** 34px head, 46px rows, sticky head, "Load more" footer, scrolls sideways inside its card. */
export function Table<Row>({ columns, rows, rowKey, onRowClick, label, hasMore, onLoadMore, loadingMore, summary, maxHeight, minWidth }: TableProps<Row>) {
  return (
    <>
      <div className={cx(s.scroll, maxHeight !== undefined && s.limited)} style={maxHeight !== undefined ? { maxHeight } : undefined} tabIndex={0} role="region" aria-label={label}>
        <table className={s.table} style={minWidth ? { minWidth } : undefined}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} scope="col" className={cx(c.align === "end" && s.num)}>
                  {c.srOnly ? <span className="sr-only">{c.header}</span> : c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)} className={cx(onRowClick && s.click)} onClick={onRowClick ? () => onRowClick(row) : undefined}>
                {columns.map((c) => (
                  <td key={c.key} className={cx(c.align === "end" && s.num)}>{c.cell(row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {summary || hasMore ? (
        <div className={s.foot}>
          <span>{summary}</span>
          {hasMore ? <Button size="sm" onClick={onLoadMore} busy={loadingMore} busyLabel="Loading…">Load more</Button> : null}
        </div>
      ) : null}
    </>
  );
}
